<template lang="pug">
  .budgeting-tool
    button.button.is-outlined.is-small(@click="togglePanel") Budgetplanung
    transition(name="slide-fade")
      .panel(v-if="visible")
        Spinner(v-if="panel.loading" :size="10" :thickness="2")
        form(v-else @submit.prevent)
          Spinner.updating(v-if="panel.updating" :size="10" :thickness="2")
          fieldset.form-field.inline
            label.label(for="budget") Geplantes Budget
            .control.has-icons-left
              input#budget.input(
                type="text"
                name="budget"
                v-model="budget"
              )
              span.icon
                | {{ currencySign }}
          .tree
            .th-category
            .th-percentage.text-center % Verteilung
            .th-should.text-center Soll
            .th-is.text-center Ist
            ProductCategoryTree(
              :tree="tree"
              :data="panel.data"
              :budget="budget"
              :currency="currency"
            )
</template>

<script>
import Vue from 'vue';

import getSymbolFromCurrency from 'currency-symbol-map';
import { mapGetters, mapMutations, mapActions } from 'vuex';

import { get, cloneDeep, debounce } from '@/helpers';
// import { moneyFormat } from '@/filters';

import ShopApi from '@/extensions/shop/store/api/shop';

import NestedSet from '@/nested-set-lib';
import ProductCategoryTree from './Partials/ProductCategoryTree';
Vue.component('ProductCategoryTree', ProductCategoryTree);

export default {
    name: 'BudgetingTool',
    data: () => ({
        categories: [],
        cartArticleMap: {},
        panel: {
            loading: false,
            updating: false,
            data: {
                budget: undefined,
                percentages: {},
                expenses: {},
                quantities: {}
            }
        }
    }),
    watch: {
        'cart.qty': debounce(function() {
            this.panel.updating = true;

            this.updateCartArticleMap();
            const articleIDs = Object.keys(this.cartArticleMap).map(id => parseInt(id));

            ShopApi.fetchArticlesInCategories({ ids: articleIDs })
                .then(this.calculateExpenses)
                .finally(() => (this.panel.updating = false));
        }, 500)
    },
    computed: {
        budget: {
            set(value) {
                value = value ? parseFloat(value) : 0;

                let budgeting = JSON.parse(localStorage.getItem('budgeting-tool')) || {};
                budgeting.budget = value;
                localStorage.setItem('budgeting-tool', JSON.stringify(budgeting));

                this.$set(this.panel.data, 'budget', value);
            },
            get() {
                return this.panel.data?.budget ? parseFloat(this.panel.data.budget) : undefined;
            }
        },
        currency() {
            const code =
                get(this.customer, 'customerGroup.currency.iso4217') ||
                get(this.defaultCustomerGroup, 'customerGroup.currency.iso4217') ||
                'EUR';

            return code;
        },
        currencySign() {
            return getSymbolFromCurrency(this.currency);
        },
        tree() {
            let tree;
            if (this.categories.length) {
                const items = cloneDeep(this.categories);
                tree = NestedSet.createTree(items);
            }
            return tree;
        },
        ...mapGetters({
            customer: 'shop/customer',
            defaultCustomerGroup: 'shop/defaultCustomerGroup',
            visible: 'shop/budgeting',
            cart: 'shop/cart'
        })
    },
    methods: {
        updateCartArticleMap() {
            this.cartArticleMap = {};

            this.cart.articlePivots.map(el => {
                if (el?.article?.id) {
                    this.$set(this.cartArticleMap, el.article.id, el.qty);
                }
            });
        },
        calculateExpenses(articles) {
            this.$set(this.panel.data, 'expenses', {});
            this.$set(this.panel.data, 'quantities', {});

            articles.forEach(article => {
                const categories = article?.product?.categories || [];
                categories.forEach(category => {
                    if (!this.panel.data.expenses[category.id]) {
                        this.$set(this.panel.data.expenses, category.id, 0);
                    }

                    let expanse = this.panel.data.expenses[category.id];
                    let quantity = this.panel.data.quantities[category.id] || 0;

                    const qty = get(this.cartArticleMap, article.id);
                    if (qty) {
                        let price = 0;
                        if (this.customer && get(this.customer, 'show_net_prices')) {
                            if (article.salePrice) {
                                price = article.salePrice.net_value;
                            } else if (article.price) {
                                price = article.price?.net_value;
                            }
                        } else {
                            if (article.salePrice) {
                                price = article.salePrice.value;
                            } else if (article.price) {
                                price = article.price?.value;
                            }
                        }
                        expanse += price * qty;
                        quantity += qty;
                    }

                    this.$set(this.panel.data.expenses, category.id, expanse);
                    this.$set(this.panel.data.quantities, category.id, quantity);
                });
            });
        },
        ...mapMutations({
            togglePanel: 'shop/toggleBudgeting'
        }),
        ...mapActions({
            fetchCategories: 'shop/fetchCategories'
        })
    },
    mounted() {
        this.panel.loading = true;

        let budgeting = JSON.parse(localStorage.getItem('budgeting-tool')) || {};
        if (budgeting.budget) this.panel.data.budget = parseFloat(budgeting.budget);
        if (budgeting.percentages) this.panel.data.percentages = budgeting.percentages;

        this.updateCartArticleMap();
        const articleIDs = Object.keys(this.cartArticleMap).map(id => parseInt(id));

        const init = () =>
            Promise.all([
                this.fetchCategories().then(categories => (this.categories = categories)),
                ShopApi.fetchArticlesInCategories({ ids: articleIDs }).then(this.calculateExpenses)
            ]);

        init().finally(() => {
            this.panel.loading = false;
        });
    }
};
</script>

<style lang="sass" scoped>
.budgeting-tool
  @apply hidden
  height: 50px
  align-items: center
  @screen md
    @apply flex
  @apply relative mx-6
  .panel
    @apply absolute rounded-none border text-black flex bg-gray-100
    bottom: calc(100% + 1rem)
    right: -10px
    min-width: 400px
    min-height: 100px
    max-height: calc(100vh - 50px - 2rem)

form
  min-width: 100%
  display: flex
  flex-direction: column
  fieldset.form-field.inline
    @apply flex
    justify-content: space-between
    align-items: center
    label
      font-weight: bold
      margin-bottom: 0

  .has-icons-left input
    &[type="text"],
    &[type="time"],
    &[type="email"],
    &[type="number"],
    &[type="password"]
      padding-left: 34px !important

#budget
  width: 150px
span.icon
  font-weight: bold
  font-size: 1.3rem !important

.tree
  overflow-y: auto
  display: grid
  grid-template-columns: auto repeat(3, max-content)
  font-size: 0.8rem
  &::v-deep
    ul, li, .product-category-tree
      display: contents
  [class^="th"]
    margin-bottom: 10px
    font-weight: bold
  .th-should, .th-is
    padding-left: 10px
    text-align: right

.spinner-container.updating
  position: absolute
  left: 0
  top: 0
  height: 100%
  width: 100%
  z-index: 1
  background: rgba(255, 255, 255, 0.6)
</style>
